/**
 * Warehouse account repair (ACT2-1286 PR 2) — shared, browser-safe types. The UI reads these through
 * the `@server/*` aliases, so this module imports nothing but zod.
 */
import { z } from 'zod';

export const REPAIR_ITEM_TYPES = ['dashboard', 'audience', 'audience_sketch', 'chat_sketch', 'workflow'] as const;
export type RepairItemType = (typeof REPAIR_ITEM_TYPES)[number];

/**
 * How a repair names its item. A chat sketch is keyed by session AND warehouse (its primary key). A
 * workflow is repaired per query node (spec "Repair"), so its ref names the node.
 */
export type RepairItemRef =
  | { itemType: 'dashboard'; id: string }
  | { itemType: 'audience'; id: string }
  | { itemType: 'audience_sketch'; audienceId: string }
  | { itemType: 'chat_sketch'; sessionId: string; warehouseId: string }
  | { itemType: 'workflow'; id: string; nodeId: string };

export const RepairItemRefSchema = z.discriminatedUnion('itemType', [
  z.object({ itemType: z.literal('dashboard'), id: z.string().min(1) }),
  z.object({ itemType: z.literal('audience'), id: z.string().min(1) }),
  z.object({ itemType: z.literal('audience_sketch'), audienceId: z.string().min(1) }),
  z.object({ itemType: z.literal('chat_sketch'), sessionId: z.string().min(1), warehouseId: z.string().min(1) }),
  z.object({ itemType: z.literal('workflow'), id: z.string().min(1), nodeId: z.string().min(1) }),
]);

/**
 * The `data.reason` every "needs repair" refusal carries, on every procedure (spec "One refusal
 * code"): oRPC `PRECONDITION_FAILED` with this reason. The UI matches on it.
 */
export const ACCOUNT_NEEDS_REPAIR_REASON = 'account_needs_repair';

/**
 * What an audience's `rs_push_error` says while its warehouse account needs repair: the save
 * succeeded in Lookout, the source-definition push was skipped. A display string, no host or key.
 */
export const AUDIENCE_PUSH_NEEDS_REPAIR =
  "Not synced: this audience's warehouse account was removed. Repair it to sync your saved changes.";

export function isNeedsRepairPushError(rsPushError: string | null | undefined): boolean {
  return rsPushError === AUDIENCE_PUSH_NEEDS_REPAIR;
}

/** Snapshot strings are cut to this length before they are stored (spec "Global constraints"). */
export const SNAPSHOT_FIELD_MAX = 256;

/**
 * The relink record a confirmed repair writes (`account_relink_snapshot`, or a query node's
 * `accountRelinkSnapshot`). It copies what the account looked like at repair time, so the notice
 * still reads right after that account is renamed or deleted.
 */
export const AccountRelinkSnapshotSchema = z.object({
  accountId: z.string(),
  displayName: z.string().max(SNAPSHOT_FIELD_MAX),
  loginUser: z.string().max(SNAPSHOT_FIELD_MAX),
  /** The repairing user's id (`users.id`), resolved to a name when the item is read. */
  actor: z.string().max(SNAPSHOT_FIELD_MAX),
  /** True when the person confirmed an account whose probe failed (dashboards and sketches only). */
  unverified: z.boolean(),
});
export type AccountRelinkSnapshot = z.infer<typeof AccountRelinkSnapshotSchema>;

/** A label for a failed probe in the Repair dialog — never control flow (spec afb4c8a). */
export type ProbeFailureKind = 'permission' | 'other';

/** The outcome of probing one account for one item. `failedAt` is the 0-based index of the failing query. */
export type ProbeVerdict =
  | { result: 'passed'; queries: number }
  | {
      result: 'failed';
      queries: number;
      failedAt: number;
      failureKind: ProbeFailureKind;
      message: string;
      /** The probe was cancelled or timed out before it finished: it proves nothing and never writes. */
      aborted?: true;
    };

/** What the dialog opened with; confirm compares-and-sets against it. */
export type SeenLink = { accountKey: string | null; lastAccountId: string | null };

export const SeenLinkSchema = z.object({
  accountKey: z.string().nullable(),
  lastAccountId: z.string().nullable(),
});
